import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TagManager from 'react-gtm-module';
import './CouldBuyFlow.scss';
import WizardHeader from '../AddMyPlaceFlow/WizardHeader';
import coinIcon from '../../../common/icons/coin.png';
import { ListingAddressObject } from '../AddMyPlaceFlow/resolveListingBuilding';
import MainStep from './steps/MainStep';
import MapPickerStep from './steps/MapPickerStep';
import SuccessStep from './steps/SuccessStep';
import {
  postBuildingCouldBuy,
  putBuildingCouldBuy,
  displayAreasFromForm,
  namedAreasFromDisplayAreas,
  criteriaFromForm,
} from './areaInterestApi';
import { useBodyScrollLock } from '../../../../hooks/useBodyScrollLock';
import { useDialogFocus } from '../../../common/useDialogFocus';
import { apiRequest } from '../../../utils/api';
import { useOpenListing } from '../../../utils/useOpenListing';
import {
  CouldBuySeed,
  CouldBuyFormState,
  CouldBuyStep,
  NamedArea,
  createInitialFormState,
} from './types';
import { LatLng } from '../../../utils/types';
import { useFlowPresenceSignal } from '../useFlowPresenceSignal';

interface CouldBuyFlowProps {
  isOpen: boolean;
  isLoggedIn: boolean;
  seed: CouldBuySeed | null;
  /** Live centre of the main map, the picker's fallback when the seed has no
   *  building coordinates (Activity FAB path, JAA-341). Null keeps the legacy
   *  Helsinki default (deliberate for the edit-area-interest path). */
  fallbackCenter: LatLng | null;
  onClose: () => void;
  // Surfaces a submit failure on the landing page's toast. Required rather than
  // optional: the toast is the only place these errors are shown now, so a
  // missing handler would swallow them silently.
  onToast: (message: string) => void;
  /** Closes the flow on success. The optional fly target recentres the map on
   *  what was created (the area's centre, or the building) after the card closes. */
  onComplete: (flyTarget?: {
    center: { lat: number; lng: number };
    zoom?: number;
  }) => void;
  setLoginModalVisible: (show: boolean) => void;
  /** When set the flow opens in edit mode with these values pre-filled (Task 5,
   *  back-fed from MyAreasView). Takes precedence over the district prefill
   *  resolution so the stored picks are shown verbatim.  */
  initialForm?: CouldBuyFormState;
  /** When set the flow is editing an existing area interest: submitInterest will
   *  PUT /area-interests/:id instead of POST to avoid creating a duplicate. */
  interestId?: string;
  /** When set the flow is editing an existing buy listing (JAA-304): the
   *  building-branch submit PUTs /off-market-willingness/:id instead of POSTing
   *  a new one. Mutually exclusive with interestId — a buy listing is always a
   *  single building, never an area interest. */
  willingnessId?: string;
  /** Keeps background map listings/counts paused through the close animation. */
  onPresenceChange?: (isPresent: boolean) => void;
  /** The expanded landing map already prevents document scrolling; applying a
   *  fixed-body lock there makes Mapbox clear its canvas for one frame. */
  lockBodyScroll: boolean;
}

const ANIMATION_MS = 600;

// Map zoom applied when the success card closes: wide enough to frame the ~1 km
// area for an area interest, tighter on the building for a single building.
const AREA_DONE_ZOOM = 14;
const BUILDING_DONE_ZOOM = 16;

// Euro-coin graphic beside the could-buy header title. Uses the shared buyer
// coin asset (coin.png), the same graphic used on listing cards and the
// building view, instead of a hand-drawn approximation.
const HeaderCoinIcon = () => (
  <img src={coinIcon} alt="" width={20} height={20} />
);

const CouldBuyFlow = ({
  isOpen,
  isLoggedIn,
  seed,
  fallbackCenter,
  onClose,
  onToast,
  onComplete,
  setLoginModalVisible,
  initialForm,
  interestId,
  willingnessId,
  onPresenceChange,
  lockBodyScroll,
}: CouldBuyFlowProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const openListing = useOpenListing();
  const [step, setStep] = useState<CouldBuyStep>('main');
  const [form, setForm] = useState<CouldBuyFormState>(createInitialFormState);
  const [isPublishing, setIsPublishing] = useState(false);
  // When the buyer taps a different building on the map picker, it replaces the
  // seed anchor for this flow. effectiveSeed below is the source of truth everywhere.
  const [anchorOverride, setAnchorOverride] = useState<CouldBuySeed | null>(
    null,
  );
  // Criteria-matched sellers already listing on the anchor building (building
  // branch only; the POST computes it with the same predicate the Activity row
  // uses). The success screen shows the count and offers a jump to that row's
  // detail — no conversation is created by the post itself.
  const [matchedListingCount, setMatchedListingCount] = useState(0);
  const [created, setCreated] = useState<{
    eventId: string;
    buildingId: string;
    address: ListingAddressObject;
  } | null>(null);
  // The area interest this flow has already written, if any. Two things need it:
  // re-publishing after Back updates that interest instead of inserting a
  // duplicate, and the success card's notify toggles have an id to save against.
  // Stays null on the building-only branch, whose POST creates an off-market
  // willingness (a different collection), so it is never confused with one.
  const [savedAreaId, setSavedAreaId] = useState<string | null>(null);
  // The most recent interest this flow session CREATED (either branch), for
  // the JAA-315 toast signal. Distinct from savedAreaId, which must survive a
  // branch switch so a Back-then-republish still PUTs instead of duplicating.
  const [lastCreated, setLastCreated] = useState<{
    kind: 'area' | 'building';
    id: string;
  } | null>(null);
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isActive, setIsActive] = useState(false);

  useBodyScrollLock(isMounted && lockBodyScroll);
  useFlowPresenceSignal(isMounted, onPresenceChange);
  // Focus moves into the dialog on open, Tab stays inside it, and the opener
  // gets focus back on close — the part aria-modal cannot do on its own.
  const dialogRef = useDialogFocus<HTMLDivElement>(isMounted && isOpen);

  // Fresh state on each open. The buyer's own district is no longer pre-selected:
  // MainStep surfaces it as a one-tap suggestion chip and reveals the nearby
  // districts only after it is picked (progressive disclosure). Gated on the
  // closed -> open transition (not on initialForm's object identity) so a parent
  // re-render that re-creates the prop inline can't wipe the buyer's edits mid-flow.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setForm(initialForm ? { ...initialForm } : createInitialFormState());
      setStep('main');
      setCreated(null);
      setSavedAreaId(null);
      setLastCreated(null);
      setIsPublishing(false);
      setAnchorOverride(null);
      setMatchedListingCount(0);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, initialForm]);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      return undefined;
    }
    setIsActive(false);
    const timer = setTimeout(() => setIsMounted(false), ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    if (!isMounted || !isOpen) return undefined;
    const raf = requestAnimationFrame(() => setIsActive(true));
    return () => cancelAnimationFrame(raf);
  }, [isMounted, isOpen]);

  // Escape is bound once per open and reads the exit through a ref, so the
  // listener never has to be re-attached as the handler's identity changes.
  const closeFlowRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeFlowRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const updateForm = useCallback((updates: Partial<CouldBuyFormState>) => {
    setForm((prev) => ({ ...prev, ...updates }));
  }, []);

  const addArea = useCallback((area: NamedArea) => {
    setForm((prev) => {
      const key = area.ext_id || area.name.toLowerCase();
      const exists = prev.areas.some(
        (a) => (a.ext_id || a.name.toLowerCase()) === key,
      );
      return exists ? prev : { ...prev, areas: [...prev.areas, area] };
    });
  }, []);

  const removeArea = useCallback((index: number) => {
    setForm((prev) => ({
      ...prev,
      areas: prev.areas.filter((_, i) => i !== index),
    }));
  }, []);

  // Apply the map picker's result to the form and return to the main screen. The
  // picker owns the full district pick set and the seed-building (circle) state,
  // so its result replaces both wholesale. When the buyer swapped the anchor to a
  // different building on the map, `anchor` carries it and replaces the seed.
  const handleMapDone = useCallback(
    (
      areas: NamedArea[],
      circleSelected: boolean,
      anchorSelected: boolean,
      anchor: CouldBuySeed | null,
    ) => {
      setForm((prev) => ({ ...prev, areas, circleSelected, anchorSelected }));
      if (anchor) setAnchorOverride(anchor);
      setStep('main');
    },
    [],
  );

  // The active anchor building: the map-swapped one if the buyer changed it, else
  // the seed the flow opened from. Source of truth for the chip, circle, and POST.
  // An area interest and a building-level buy listing are the same thing at
  // different widths, so "am I editing" is one question. Only the submit branch
  // and the locked anchor care which of the two ids it is.
  const isEditing = !!interestId || !!willingnessId;

  const effectiveSeed = anchorOverride ?? seed;
  const buildingAddress = effectiveSeed
    ? [effectiveSeed.address.street, effectiveSeed.address.houseNumber]
        .filter(Boolean)
        .join(' ')
        .trim()
    : '';
  const buildingArea = effectiveSeed?.address.city ?? '';

  // Closing the success card flies the map to what was created: the area's centre
  // (zoomed out to frame it) for an area interest, otherwise the building. The
  // anchor IS the centre — exact for the 500 m circle, the building's location for
  // districts (NamedArea carries no geometry on the FE).
  // Latched so the announcement fires once per publish however the flow is
  // left; reset when it reopens.
  const announcedRef = useRef(false);
  useEffect(() => {
    if (isOpen) announcedRef.current = false;
  }, [isOpen]);

  // Fresh interest published: tell the app shell, which closes the building
  // page the flow opened over and raises the JAA-315 success toast. A no-op
  // before a publish and when editing, so every exit can call it blindly.
  const announceCreated = useCallback(() => {
    if (isEditing || !lastCreated || announcedRef.current) return;
    announcedRef.current = true;
    window.dispatchEvent(
      new CustomEvent('jaakko:buying-interest-created', {
        detail: lastCreated,
      }),
    );
  }, [isEditing, lastCreated]);

  // The single way out. Done, the header's X and Escape all publish the same
  // interest when they land on the success card, so all three announce before
  // closing — otherwise dismissing the card with X left the building page open
  // and the toast unraised.
  const closeFlow = useCallback(() => {
    announceCreated();
    onClose();
  }, [announceCreated, onClose]);
  useEffect(() => {
    closeFlowRef.current = closeFlow;
  }, [closeFlow]);

  const handleDone = useCallback(() => {
    announceCreated();
    const center = effectiveSeed?.coordinates ?? null;
    const isArea = form.circleSelected || form.areas.length > 0;
    onComplete(
      center
        ? { center, zoom: isArea ? AREA_DONE_ZOOM : BUILDING_DONE_ZOOM }
        : undefined,
    );
    onClose();
  }, [
    announceCreated,
    onComplete,
    onClose,
    effectiveSeed,
    form.circleSelected,
    form.areas.length,
  ]);

  const handleBack = useCallback(() => {
    switch (step) {
      case 'map':
        setStep('main');
        break;
      case 'success':
        setStep('main');
        break;
      case 'main':
      default:
        onClose();
        break;
    }
  }, [step, onClose]);

  // Map a submit failure to a user-facing message. apiRequest throws the parsed
  // backend error body, so prefer the known business-rule code (translated), then
  // the backend's own `message`/`error` text so the real reason is shown instead of
  // a generic "something went wrong", and only fall back to the generic string last.
  // The area-interest validation codes arrive without a message: too many areas
  // gets its own actionable copy, the rest the generic submit error.
  const submitErrorMessage = (error: unknown): string => {
    const body = error as {
      error_code?: string;
      message?: string;
      error?: string;
    } | null;
    // Returned as the toast's own outcome code rather than resolved text: the
    // toast owns the copy for the codes it knows, so there is one place where
    // this rule is worded.
    if (body?.error_code === 'USER_SELLER_AND_BUYER') {
      return 'USER_SELLER_AND_BUYER';
    }
    if (body?.error_code === 'AREAS_TOO_MANY') {
      return t('newLandingPage.couldBuy.tooManyAreas');
    }
    if (
      body?.error_code === 'AREAS_ONE_CIRCLE' ||
      body?.error_code === 'AREAS_INVALID' ||
      body?.error_code === 'CRITERIA_INVALID'
    ) {
      return t('landingPage.couldBuyOrCouldSellModal.submitError');
    }
    return (
      body?.message ||
      body?.error ||
      t('landingPage.couldBuyOrCouldSellModal.submitError')
    );
  };

  // Boost request for the interest that was just posted. Notifies the team via
  // Slack + email and charges nothing on its own; the user confirms inside
  // BoostModal. Mirrors the Activity row detail's handler.
  const handleBoostConfirm = async () => {
    if (!created) return;
    await apiRequest('/boost-listing', {
      method: 'POST',
      body: {
        eventId: created.eventId,
        buildingId: created.buildingId,
        actionType: 'buy',
        buildingAddress,
      },
    });
  };

  // Push the legacy buy submit event the buyer ad conversion in Tag Manager
  // listens to, once a create has been saved. Unlike the old UI, which pushed on
  // click before the request, a failed save pushes nothing: the conversion tag
  // sets a one-time browser flag. At most once per open, so edits and a
  // Back-then-republish (lastCreated already set) push nothing. The name follows
  // the entry like the old UI: opened from a building is the building modal,
  // anything else the landing page. It reads the original seed, not the
  // map-swapped anchor, so an Activity FAB entry (blank building id) stays the
  // landing page even after the buyer picks a building on the map.
  const pushBuySubmitEvent = () => {
    if (isEditing || lastCreated) return;
    const fromBuilding = Boolean(seed?.buildingId);
    TagManager.dataLayer({
      dataLayer: {
        event: fromBuilding
          ? 'buy_submit_building_modal'
          : 'buy_submit_landing_page',
        category: 'User Interaction',
        action: 'Click',
        label: fromBuilding
          ? 'Buy Submit Building Modal'
          : 'Buy Submit Landing Page',
      },
    });
  };

  // Create or update the SINGLE-BUILDING "could buy" interest on the anchor
  // building (the default branch when the buyer did NOT expand the area). This
  // is alert-only, exactly like the area branch: the POST creates no
  // conversation, and the buyer opens chats per listing from the Activity row
  // detail. Both create and edit land on the success card; a PUT returns no
  // matched_listing_count, so an edit shows the plain Done CTA. Reports the
  // create to Tag Manager through pushBuySubmitEvent.
  const submitBuildingInterest = async () => {
    if (!effectiveSeed) return;
    setIsPublishing(true);
    try {
      const result = willingnessId
        ? await putBuildingCouldBuy(willingnessId, form)
        : await postBuildingCouldBuy(effectiveSeed, form);
      window.dispatchEvent(new Event('jaakko:listings-changed'));
      setMatchedListingCount(result?.matched_listing_count ?? 0);
      if (result?.willingness_id) {
        setCreated({
          eventId: result.willingness_id,
          buildingId: effectiveSeed.buildingId,
          address: effectiveSeed.address as ListingAddressObject,
        });
      }
      if (result?.willingness_id) {
        setLastCreated({ kind: 'building', id: result.willingness_id });
      }
      pushBuySubmitEvent();
      setStep('success');
    } catch (error) {
      console.error('Error creating building could-buy interest:', error);
      onToast(submitErrorMessage(error));
    } finally {
      setIsPublishing(false);
    }
  };

  // The POST/PUT payload for an area interest, built from a form state. Shared by
  // the publish submit and the success card's notify toggles, so a notify-only
  // save resends an identical area footprint: the backend keys the digest
  // watermark off whether that footprint changed (routes/area_interests.py).
  const areaRequestBody = useCallback(
    (state: CouldBuyFormState) => ({
      // Only send the anchor building when the 500 m circle is part of the
      // interest: the circle is drawn around that building, so it is the one
      // thing that needs it. A district-only interest covers a whole area and
      // the building the buyer happened to start from says nothing about it —
      // sending it made the listing page offer a "Could sell" seeded with
      // someone else's address. Same condition displayAreasFromForm uses to
      // emit the circle, so the two can't disagree.
      anchor_building_id:
        state.circleSelected && state.anchorSelected && effectiveSeed
          ? effectiveSeed.buildingId
          : null,
      // The address the browser already resolved off the map. The server can only
      // derive one from `buildings`, so for a building it never stored the card
      // read "500 m radius" forever, even though this screen knew the street
      // (JAA-328). Server-resolved still wins; this is the fallback.
      anchor_address:
        state.circleSelected &&
        state.anchorSelected &&
        effectiveSeed?.address.street
          ? {
              street: effectiveSeed.address.street,
              houseNumber: effectiveSeed.address.houseNumber,
              city: effectiveSeed.address.city,
            }
          : null,
      // The circle's centre rides along in the payload. The seed wins whenever it
      // has coordinates — on create, and on an edit where the buyer re-picked the
      // building, which must move the circle with it. The stored centre is the
      // fallback for the edit seed, which carries none (JAA-328).
      areas: displayAreasFromForm({
        ...state,
        circleCentre: effectiveSeed?.coordinates ?? state.circleCentre ?? null,
      }),
      criteria: criteriaFromForm(state),
      notify_email: state.notifyEmail,
      notify_sms: state.notifySms,
    }),
    [effectiveSeed],
  );

  // The building-level buy interest this flow is editing or has created, when
  // any. The digest notify toggles and the "view matches" CTA both hang off it.
  const buildingWillingnessId =
    willingnessId ?? (lastCreated?.kind === 'building' ? lastCreated.id : null);

  // Persist a notify-channel choice made on the success card. The interest was
  // already written by the submit that opened that step, so the toggle has to send
  // its own update; before this it moved local state only and the choice was
  // silently dropped (PR #116 N8). Optimistic: the box flips at once and the form
  // rolls back if the save fails.
  const handleNotifyChange = useCallback(
    async (updates: Partial<CouldBuyFormState>) => {
      const targetId = interestId ?? savedAreaId;
      const previous = form;
      const next = { ...form, ...updates };
      setForm(next);
      try {
        if (targetId) {
          await apiRequest(`/area-interests/${targetId}`, {
            method: 'PUT',
            body: areaRequestBody(next),
          });
        } else if (buildingWillingnessId) {
          // A building-level interest carries its own digest notify flags on
          // the willingness doc, exactly like an area interest's document —
          // flags only, so the save cannot touch criteria (and so cannot reset
          // the digest watermark).
          await apiRequest(`/off-market-willingness/${buildingWillingnessId}`, {
            method: 'PUT',
            body: {
              notify_email: next.notifyEmail,
              notify_sms: next.notifySms,
            },
          });
        }
      } catch (error) {
        console.error('Error saving could-buy notify preferences:', error);
        setForm(previous);
      }
    },
    [interestId, savedAreaId, buildingWillingnessId, form, areaRequestBody],
  );

  // Create the AREA interest and advance to success. Posts the JAA-277 contract:
  // the anchor building id, the chosen areas (ids + the fixed circle), optional
  // criteria, and notify channels. The backend resolves each area to a polygon
  // and unions them; the FE sends ids only. Notify-only, no conversation.
  // Reports the create to Tag Manager through pushBuySubmitEvent.
  const submitAreaInterest = async () => {
    if (!effectiveSeed) return;
    setIsPublishing(true);
    try {
      const location = effectiveSeed.coordinates;
      const addressObject: ListingAddressObject = {
        country: effectiveSeed.address.country || '',
        region: '',
        city: effectiveSeed.address.city || '',
        postcode: '',
        street: effectiveSeed.address.street || '',
        houseNumber: effectiveSeed.address.houseNumber || '',
        coordinates: location ?? { lat: 0, lng: 0 },
      };

      const requestBody = areaRequestBody(form);

      // Editing a stored interest, or re-publishing one this flow already created
      // (Back from the success card), both update in place. A second POST would
      // insert a duplicate interest.
      const targetId = interestId ?? savedAreaId;
      const data = targetId
        ? await apiRequest(`/area-interests/${targetId}`, {
            method: 'PUT',
            body: requestBody,
          })
        : await apiRequest('/area-interests', {
            method: 'POST',
            body: requestBody,
          });

      // Keep later saves in sync with the stored selection: the server may
      // collapse legacy/canonical aliases into fewer named areas.
      if (Array.isArray(data?.display_areas)) {
        setForm((previous) => ({
          ...previous,
          areas: namedAreasFromDisplayAreas(data.display_areas),
        }));
      }

      const resolvedId = data?.area_interest_id ?? targetId;
      if (resolvedId) {
        setSavedAreaId(resolvedId);
        setLastCreated({ kind: 'area', id: resolvedId });
        setCreated({
          eventId: resolvedId,
          buildingId: effectiveSeed.buildingId,
          address: addressObject,
        });
      }

      window.dispatchEvent(new Event('jaakko:listings-changed'));
      pushBuySubmitEvent();
      setStep('success');
    } catch (error) {
      console.error('Error creating could-buy interest:', error);
      onToast(submitErrorMessage(error));
    } finally {
      setIsPublishing(false);
    }
  };

  // Branch the submit on whether the buyer expanded their interest area. No area
  // added (no circle, no district) -> a single-building off-market interest;
  // expanded -> an area interest. Editing an existing area interest always takes
  // the area branch (you can only reach edit from a stored area interest); editing
  // an existing buy listing always takes the building branch instead — it's the
  // off-market willingness record, which has no area-interest representation.
  // With no building to anchor on (an unseeded or district-only start) and no
  // area added, it asks for a location instead of posting an empty building.
  const submitInterest = async () => {
    setMatchedListingCount(0);
    if (!effectiveSeed) {
      onToast(t('landingPage.couldBuyOrCouldSellModal.submitError'));
      return;
    }
    const expanded = displayAreasFromForm(form).length > 0;
    if ((!expanded || willingnessId) && !interestId) {
      // Nothing selected: no building (unselected, unseeded or district-only) and no area.
      if (!form.anchorSelected || !effectiveSeed.buildingId) {
        onToast(t('newLandingPage.couldBuy.main.selectLocationError'));
        return;
      }
      await submitBuildingInterest();
      return;
    }
    await submitAreaInterest();
  };

  const handlePublish = () => {
    if (isPublishing) return;
    if (isLoggedIn) {
      submitInterest();
    } else {
      sessionStorage.setItem('marketing_consent_deferred', '1');
      setLoginModalVisible(true);
    }
  };

  // Header label for the current step. The success step confirms what just
  // happened instead of still asking the buyer to create an interest; in edit
  // mode (interestId set, the flow PUTs an existing area interest) nothing was
  // published, so it says updated. Every other step keeps the create title.
  const resolveHeaderTitle = (): string => {
    if (step !== 'success') {
      return t(
        isEditing
          ? 'newLandingPage.couldBuy.editTitle'
          : 'newLandingPage.couldBuy.title',
      );
    }
    if (isEditing) return t('newLandingPage.couldBuy.titleUpdated');
    return t('newLandingPage.couldBuy.titlePublished');
  };

  const headerTitle = resolveHeaderTitle();
  const successRepresentsAreaInterest =
    !!interestId || displayAreasFromForm(form).length > 0;
  const successAreaInterestId = successRepresentsAreaInterest
    ? (interestId ?? savedAreaId ?? undefined)
    : undefined;

  if (!isMounted) return null;

  let stepNode: React.ReactNode = null;
  switch (step) {
    case 'main':
      stepNode = (
        <MainStep
          form={form}
          buildingLabel={buildingAddress}
          hasAnchor={!!effectiveSeed?.buildingId}
          coordinates={effectiveSeed?.coordinates ?? null}
          onChange={updateForm}
          onAddArea={addArea}
          onRemoveArea={removeArea}
          onToggleCircle={(on) => updateForm({ circleSelected: on })}
          onOpenMap={() => setStep('map')}
          onPublish={handlePublish}
          isPublishing={isPublishing}
          lockedToBuilding={!!willingnessId}
          isEdit={isEditing}
        />
      );
      break;
    case 'map':
      stepNode = effectiveSeed ? (
        <MapPickerStep
          seed={effectiveSeed}
          fallbackCenter={fallbackCenter}
          selectedAreas={form.areas}
          circleSelected={form.circleSelected}
          anchorSelected={form.anchorSelected}
          onDone={handleMapDone}
        />
      ) : null;
      break;
    case 'success':
      stepNode = (
        <SuccessStep
          form={form}
          areaInterestId={successAreaInterestId}
          onOpenInterest={
            successAreaInterestId
              ? (listing) => {
                  announceCreated();
                  onComplete();
                  onClose();
                  openListing(listing);
                }
              : undefined
          }
          anchorLabel={buildingAddress}
          matchedListingCount={matchedListingCount}
          isEditing={isEditing}
          onBoostConfirm={created ? handleBoostConfirm : undefined}
          onChange={handleNotifyChange}
          onDone={handleDone}
          onViewMatches={
            buildingWillingnessId
              ? () => {
                  announceCreated();
                  onComplete();
                  onClose();
                  // The digest email's deep link, reused: opens the Activity
                  // view on this interest's row detail, where each matching
                  // listing offers its own chat.
                  history.push(`/activities?interest=${buildingWillingnessId}`);
                }
              : undefined
          }
        />
      );
      break;
    default:
      stepNode = null;
  }

  // Portalled to <body> so the overlay escapes NewLandingPage's stacking context
  // and sits above the modal stack + nav (z 2600, see CouldBuyFlow.scss). Lets the
  // flow open over the building modal without closing it (closing it blanks NLP).
  return createPortal(
    <div className="cbf-flow__overlay" data-open={isActive ? 'true' : 'false'}>
      <div
        className={[
          'cbf-flow',
          // Every step but the map sizes to its content; the map's canvas is
          // height:100% and would collapse.
          step === 'map' ? '' : 'cbf-flow--fit',
          // Editing is a short sheet, so its CTA sizes to its label rather than
          // spanning the width like the create flow's floating primary.
          willingnessId || interestId ? 'cbf-flow--edit' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={headerTitle}
      >
        <WizardHeader
          title={headerTitle}
          titleIcon={<HeaderCoinIcon />}
          hideBack={step !== 'map'}
          onBack={handleBack}
          onClose={closeFlow}
          activeDot={null}
          activeDotDone={false}
          totalDots={0}
        />
        <div className={`cbf-flow__content cbf-flow__content--${step}`}>
          {stepNode}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CouldBuyFlow;
